import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { certificatesApi, CertificateDataResponse } from '../api/certificates';

export default function CertificateViewPage() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<CertificateDataResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Set language from query param
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang && ['en', 'es', 'ru', 'lt'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    if (!code) return;

    certificatesApi
      .getCertificateData(code)
      .then(setData)
      .catch(() => setError('Failed to load certificate data'));
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="h-screen bg-white p-6 print:p-0 flex items-center justify-center">
        {/* Certificate container */}
        <div className="w-full max-w-[180mm] bg-white border-8 border-double border-gray-800 p-10">
          {/* Header with logo */}
          <div className="text-center mb-8">
            <img
              src="/landing/logo-light.svg"
              alt="Fonorex"
              className="h-12 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-widest">
              {t('certificate.title')}
            </h1>
            <p className="text-lg text-gray-600 mt-2">
              {t('certificate.subtitle')}
            </p>
          </div>

          {/* Decorative line */}
          <div className="border-t-2 border-gray-300 my-6" />

          {/* Certificate body */}
          <div className="text-center space-y-5">
            <p className="text-lg text-gray-700">
              {t('certificate.certifies')}
            </p>

            <h2 className="text-4xl font-bold text-gray-900 py-3">
              {data.venueName}
            </h2>

            {data.venueAddress && (
              <p className="text-lg text-gray-600">
                {data.venueAddress}
              </p>
            )}

            <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
              {t('certificate.grantedRights')}
            </p>

            {/* Validity period */}
            <div className="bg-gray-50 rounded-lg p-5 inline-block mt-6">
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">
                {t('certificate.validityPeriod')}
              </p>
              <p className="text-xl font-semibold text-gray-900">
                {formatDate(data.periodStart)} — {formatDate(data.periodEnd)}
              </p>
            </div>
          </div>

          {/* Decorative line */}
          <div className="border-t-2 border-gray-300 my-6" />

          {/* QR Code and verification info */}
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-sm text-gray-500 mb-1">
                {t('certificate.email')}
              </p>
              <p className="text-gray-900">{data.email}</p>

              <p className="text-sm text-gray-500 mt-4 mb-1">
                {t('certificate.verificationCode')}
              </p>
              <p className="text-2xl font-mono font-bold text-gray-900">
                {data.verificationCode}
              </p>
            </div>

            <div className="text-center">
              <QRCodeSVG
                value={data.verifyUrl}
                size={110}
                level="M"
                className="mx-auto"
              />
              <p className="text-xs text-gray-500 mt-2 max-w-[140px]">
                {t('certificate.scanToVerify')}
              </p>
            </div>

            <div className="text-right">
              <img
                src="/landing/logo-light.svg"
                alt="Fonorex"
                className="h-8 ml-auto mb-2"
              />
              <p className="text-sm text-gray-600">Corvax MB</p>
              <p className="text-xs text-gray-500">Kaunas, Lithuania</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-5 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              {t('certificate.verificationNote')}
            </p>
            <p className="text-sm text-gray-600 font-mono mt-1">
              fonorex.com/verify/{data.verificationCode}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
